import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Naming the voice that shrinks me",
    opening: localizedGenderNode([
      "There is a voice in me that does not always shout.",
      "Sometimes it just lowers me a little.",
      "A little less worthy.",
      "A little less enough.",
      "A little less valuable.",
      "And almost without noticing,",
      "I already believe it.",
    ]),
    mindfulness: localizedGenderNode([
      "A moment.",
      "I am not arguing with the voice right now.",
      "I am just noticing that it is already here.",
    ]),
    prompts: localizedGenderNode([
      "The moments when this voice is already here inside me.",
      "A sentence that loops in me without my choosing it.",
      "In my body, when I already believe it—something there.",
    ]),
    closing: localizedGenderNode([
      "This voice is already familiar.",
      "A little space opens between me and it.",
    ]),
    action: localizedGenderNode(
      "Today I pause once and say quietly: this is a voice, not all of who I am.",
      "Today I pause once and say quietly: this is a voice, not all of who I am."
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "The moment my worth drops",
    opening: localizedGenderNode([
      "Sometimes it happens fast.",
      "One reaction.",
      "One comparison.",
      "One look.",
      "And something in me already falls.",
      "As if my worth depended on this moment.",
    ]),
    mindfulness: localizedGenderNode([
      "I stay for a moment right at that drop.",
      "Before everything closes around me.",
    ]),
    prompts: localizedGenderNode([
      "The beat before I already feel less—something there.",
      "The first thing that rises there, in me.",
      "Something inside that pushes the fall too fast.",
    ]),
    closing: localizedGenderNode([
      "I saw the moment I already fall inside.",
      "It matters to me to see it.",
    ]),
    action: localizedGenderNode(
      "Today I notice the first point where my worth starts to sink.",
      "Today I notice the first point where my worth starts to sink."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "How it sits in the body",
    opening: localizedGenderNode([
      "This feeling does not stay only in my head.",
      "My chest sinks.",
      "My belly tightens.",
      "My gaze drops.",
      "I take up less space.",
      "As if my body already accepted the verdict.",
    ]),
    mindfulness: localizedGenderNode([
      "I feel how my body holds this shrinking.",
    ]),
    prompts: localizedGenderNode([
      "The \"less\" feeling—where it sits in my body right now.",
      "My body there—like it is guarding something.",
      "A little softness—breath, a hand—how that feels inside.",
    ]),
    closing: localizedGenderNode([
      "My body holds a lot of this feeling.",
      "I stayed with it for a moment.",
    ]),
    action: localizedGenderNode(
      "Today I place a hand on my body and take one breath when I pull inward.",
      "Today I place a hand on my body and take one breath when I pull inward."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "How I already prove to myself I am less",
    opening: localizedGenderNode([
      "I am starting to see this pattern.",
      "I collect evidence against myself.",
      "I remember more of what did not work.",
      "I shrink what did.",
      "And I build myself a story",
      "that explains why I am less.",
    ]),
    mindfulness: localizedGenderNode([
      "I pause between what happened",
      "and how I already use it against myself.",
    ]),
    prompts: localizedGenderNode([
      "The way I prove to myself again that I am less.",
      "What I blow up against myself without noticing.",
      "What works in me—and I barely stay with it.",
    ]),
    closing: localizedGenderNode([
      "I saw the way I build this.",
      "It is a little less invisible to me now.",
    ]),
    action: localizedGenderNode(
      "Today I mark one moment when I use something against myself again.",
      "Today I mark one moment when I use something against myself again."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "fear",
    title: "What I am afraid this says about me",
    opening: localizedGenderNode([
      "Underneath this drop in my sense of worth,",
      "there is fear too.",
      "That if I am not enough here,",
      "maybe I am simply not enough at all.",
      "That this is not a moment.",
      "That this is who I am.",
    ]),
    mindfulness: localizedGenderNode([
      "I stay for a moment with the meaning I give this.",
    ]),
    prompts: localizedGenderNode([
      "The fear that tells me what this moment says about me.",
      "An old sentence that wakes up there inside.",
      "The hottest part of this fear.",
    ]),
    closing: localizedGenderNode([
      "I heard the fear under the shrinking.",
      "It touches deep into my sense of worth.",
    ]),
    action: localizedGenderNode(
      "Today I name this fear once, quietly.",
      "Today I name this fear once, quietly."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "inner_world",
    title: "The part that already believes it",
    opening: localizedGenderNode([
      "There is also a part of me that got used to this.",
      "That already believes I am less.",
      "That does not expect much.",
      "That is not surprised when I close again.",
      "It does not shout.",
      "It has just lived there a long time.",
    ]),
    mindfulness: localizedGenderNode([
      "I see the part that already believes this story.",
    ]),
    prompts: localizedGenderNode([
      "The place in me that already believes I am less.",
      "That part—how it touches my choices.",
      "Something that part asks of me without words.",
    ]),
    closing: localizedGenderNode([
      "There is a part of me that has held this belief for a long time.",
      "I want to see it, not only fight it.",
    ]),
    action: localizedGenderNode(
      "Today I notice one moment when this part is steering me.",
      "Today I notice one moment when this part is steering me."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "What I really need underneath this",
    opening: localizedGenderNode([
      "Behind all of this",
      "there is also a simple need.",
      "To feel worthy.",
      "To feel I have a place.",
      "To feel I do not have to prove all the time",
      "to stay okay.",
    ]),
    mindfulness: localizedGenderNode([
      "I listen to this need without belittling it.",
    ]),
    prompts: localizedGenderNode([
      "Under the \"less\"—a need there, without asking out loud.",
      "A little steadiness inside—how that sounds to me when I pause.",
      "A gentle ask of myself—how that feels inside.",
    ]),
    closing: localizedGenderNode([
      "There is a real need in me to feel worth.",
      "That need gets a small place too.",
    ]),
    action: localizedGenderNode(
      "Today I give this need one small moment of room.",
      "Today I give this need one small moment of room."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Not talking down to myself",
    opening: localizedGenderNode([
      "When I feel less,",
      "my tone with myself turns cold.",
      "Dismissing.",
      "From above.",
      "As if I am already a clear disappointment.",
      "And that pulls me down even more.",
    ]),
    mindfulness: localizedGenderNode([
      "I hear how I speak to myself inside this fall.",
    ]),
    prompts: localizedGenderNode([
      "My tone with myself when I already feel less.",
      "Words I miss there, under the cold tone.",
      "A sentence that shrinks me less—and still feels true inside.",
    ]),
    closing: localizedGenderNode([
      "My tone changes the whole depth of the fall.",
      "That stays with me like this.",
    ]),
    action: localizedGenderNode(
      "Today I say one sentence to myself that does not talk down to me.",
      "Today I say one sentence to myself that does not talk down to me."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "perspective",
    title: "Not every weakness is proof",
    opening: localizedGenderNode([
      "Maybe not every hesitation means I am weak.",
      "Not every mistake means I am less.",
      "Not every struggle is proof against me.",
      "Sometimes I jump too fast",
      "from what happened",
      "to what it says I am.",
    ]),
    mindfulness: localizedGenderNode([
      "I return to what actually happened",
      "and not only to what I already concluded about myself.",
    ]),
    prompts: localizedGenderNode([
      "What happened—next to what the feeling already told me about myself.",
      "The conclusion I jump to too fast about myself.",
      "Another small possibility—just present there, without deciding.",
    ]),
    closing: localizedGenderNode([
      "Something else was there with me beside what happened.",
      "A little less hardness inside.",
    ]),
    action: localizedGenderNode(
      "Today I hold one extra possibility besides \"I am less.\"",
      "Today I hold one extra possibility besides \"I am less.\""
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "choice",
    title: "The moment before I shrink myself",
    opening: localizedGenderNode([
      "There is a moment when I already do it alone.",
      "I shrink myself before anyone else has even said anything.",
      "I give up in advance.",
      "I pull in in advance.",
      "And I want to see that moment.",
    ]),
    mindfulness: localizedGenderNode([
      "I pause before I lower myself inside again.",
    ]),
    prompts: localizedGenderNode([
      "The shrinking that comes before anything even happens.",
      "Something I am trying to protect when I shrink in advance.",
      "Before the auto-pilot shrink—one small beat I notice in me.",
    ]),
    closing: localizedGenderNode([
      "Early shrinking shows up a little clearer now.",
      "I just paused beside it.",
    ]),
    action: localizedGenderNode(
      "Today I stay one more beat before I shrink myself.",
      "Today I stay one more beat before I shrink myself."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "perspective",
    title: "Seeing something alive in me too",
    opening: localizedGenderNode([
      "When I am inside this feeling,",
      "I only see what I lack.",
      "But there are also things in me that are alive.",
      "Things that stay.",
      "Things that do not disappear just because I had a hard moment.",
    ]),
    mindfulness: localizedGenderNode([
      "I stay for a moment with what is still alive in me—even when I do not feel strong.",
    ]),
    prompts: localizedGenderNode([
      "Something still alive in me, even on a day like this.",
      "A part of me I barely make room for when my worth drops.",
      "Something I bring with me—even when I forget.",
    ]),
    closing: localizedGenderNode([
      "There is more in me than the fall.",
      "Even when I do not feel it.",
    ]),
    action: localizedGenderNode(
      "Today I remember one living thing in me—without having to prove it outside.",
      "Today I remember one living thing in me—without having to prove it outside."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "strength",
    title: "Staying with one thing in me that was not taken",
    opening: localizedGenderNode([
      "Maybe my worth does not have to rise and fall with every reaction.",
      "Maybe there is something in me that stays mine",
      "even when I am confused.",
      "even when I fall.",
      "even when I have no proof from outside.",
    ]),
    mindfulness: localizedGenderNode([
      "I stay for a moment with what in me is not taken from me that fast.",
    ]),
    prompts: localizedGenderNode([
      "Something that stays mine without outside approval.",
      "Moments when it is easier for me to remember that.",
      "When my worth drops again—something in me that returns to it.",
    ]),
    closing: localizedGenderNode([
      "There is something in me that is not all tied to these falls.",
      "A small place of belonging stays.",
    ]),
    action: localizedGenderNode(
      "Today I return to one thing in me that was not taken from me.",
      "Today I return to one thing in me that was not taken from me."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "A small step that does not come from shrinking",
    opening: localizedGenderNode([
      "I do not need a big turnaround right now.",
      "I need one small step",
      "that does not come out of me being less.",
      "A real step.",
      "Small.",
      "But not dismissed inside.",
    ]),
    mindfulness: localizedGenderNode([
      "I look for the small step I can take without shrinking myself on the way.",
    ]),
    prompts: localizedGenderNode([
      "A small step that still fits without leaving the shrink.",
      "A small fear that stirs around this step.",
      "Staying with this without full confidence—how that feels inside.",
    ]),
    closing: localizedGenderNode([
      "I chose a small step not built on shrinking myself.",
      "That is already a different direction.",
    ]),
    action: localizedGenderNode(
      "Today I go with this small step even if I am not fully sure.",
      "Today I go with this small step even if I am not fully sure."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Continuing with a little more worth inside",
    opening: localizedGenderNode([
      "This feeling will still come back sometimes.",
      "I know that.",
      "But I do not have to fall with it straight into \"I am less.\"",
      "Today I have a little more way",
      "to come back to myself even inside this.",
    ]),
    mindfulness: localizedGenderNode([
      "I breathe and return to what in me is not only this moment.",
    ]),
    prompts: localizedGenderNode([
      "The \"less\" feeling—how it introduces itself to me now.",
      "First sign it is rising—I recognize it.",
      "A short line that is starting to stay with me.",
    ]),
    closing: localizedGenderNode([
      "I continue from here with a little more worth inside.",
    ]),
    action: localizedGenderNode(
      "Today I keep one short sentence that brings me from shrinking back toward worth.",
      "Today I keep one short sentence that brings me from shrinking back toward worth."
    ),
  },
].map(buildNormalizedDay);

export const lowSelfEsteemNormalizedBlueprintEn = buildNormalizedPilotBlueprint({
  id: "low_self_esteem_14d",
  version: "normalizer_locale_en_v4",
  durationDays: 14,
  problemKey: "low_self_esteem",
  category: "self_worth",
  journeyName: {
    he: "ערך עצמי נמוך",
    en: "Low self-esteem",
    es: "Baja autoestima",
  },
  days,
});

export default lowSelfEsteemNormalizedBlueprintEn;
